import { Line } from '@react-three/drei';
import { type ThreeEvent, useFrame, useThree } from '@react-three/fiber';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type Camera,
  type Object3D,
  type OrthographicCamera,
  Plane,
  Raycaster,
  Vector2,
  Vector3,
} from 'three';

import {
  usePlayerCameraControls,
  usePlayerResolvedAnimation,
  usePlayerStoreApi,
} from '@cartwhl/3d/player';

import { KEY_POSE_TRANSFORM_CONTROLS_SIZE } from '../keyPoses/constants';
import { getSourceBvhForTimeline } from '../keyPoses/keyPoseFrameMapping';
import { resolveKeyPoseMotionContext } from '../keyPoses/keyPoseMotionUtils';
import { useStore as useMotionEditorStore } from '../motionEditorStore';
import {
  REPATHING_COLORS,
  REPATHING_CONTROL_Y,
  REPATHING_CURVE_LINE_WIDTH,
  REPATHING_CURVE_Y,
  REPATHING_EVAL_STEPS,
  REPATHING_HANDLE_LINE_WIDTH,
  REPATHING_HANDLE_Y,
  REPATHING_RENDER_ORDER,
  REPATHING_SOURCE_LINE_WIDTH,
  REPATHING_SOURCE_Y,
} from './constants';
import { sampleRepathingCurve } from './repathingCurve';
import { useStore } from './repathingStore';
import type {
  RepathingActionSnapshot,
  RepathingControlHandle,
  RepathingControlPoint,
  RepathingControlSelection,
  RepathingPoint2d,
} from './types';

type CameraControlsWithEnabled = {
  enabled?: boolean;
  cancel?: () => void;
  stop?: () => void;
};

const REPATHING_POINTER_PLANE_SIZE = 200;
const REPATHING_POINT_RADIUS = 0.035;
const REPATHING_MARKER_BORDER_SCALE = 1.175;
const REPATHING_HANDLE_RADIUS = 0.026;
const REPATHING_HANDLE_HIT_RADIUS = 0.026;
const REPATHING_MARKER_FAR_SCALE_DAMPING = 0.2;
const REPATHING_MARKER_MAX_WORLD_SCALE = 1.2;
const REPATHING_DRAG_PLANE_NORMAL = new Vector3(0, 1, 0);
const ignoreRaycast = () => {};

export default function RepathingSceneLayer() {
  const store = useStore();
  const motionEditorStore = useMotionEditorStore();
  const { controlsMode, curve, sourceCurve, tool } = store.state;
  const motionContext = resolveKeyPoseMotionContext({
    scene: motionEditorStore.state.scene,
    playback: motionEditorStore.state.playback,
  });
  const resolvedAnimation = usePlayerResolvedAnimation(
    motionContext?.referenceName,
  );
  const sourceBvh = getSourceBvhForTimeline(
    resolvedAnimation.bvh,
    resolvedAnimation.bvhs,
    motionContext?.timelineIndex,
  );
  const sourceKey = useMemo(() => {
    if (!motionContext || !sourceBvh) return null;

    return [
      motionContext.sceneID,
      motionContext.referenceName,
      motionContext.timelineIndex,
      motionContext.motionID,
      motionContext.appliedMotionEditJobID ?? '',
      motionContext.bvhUrls.join('|'),
      sourceBvh.clip.uuid,
    ].join('::');
  }, [motionContext, sourceBvh]);

  useEffect(() => {
    if (controlsMode !== 'path') {
      return;
    }
    if (!sourceBvh || !sourceKey) {
      if (!resolvedAnimation.isLoading) {
        store.clearCurve();
      }
      return;
    }

    store.ensureCurveFromBvh(sourceBvh, sourceKey);
  }, [controlsMode, resolvedAnimation.isLoading, sourceBvh, sourceKey, store]);

  const curveSamples = useMemo(
    () => (curve ? sampleRepathingCurve(curve, REPATHING_EVAL_STEPS) : []),
    [curve],
  );
  const sourceSamples = useMemo(
    () =>
      sourceCurve
        ? sampleRepathingCurve(sourceCurve, REPATHING_EVAL_STEPS)
        : [],
    [sourceCurve],
  );

  if (controlsMode !== 'path' || !curve) {
    return null;
  }

  return (
    <group>
      {sourceSamples.length > 1 ? (
        <RepathingPathLine
          color={REPATHING_COLORS.source}
          lineWidth={REPATHING_SOURCE_LINE_WIDTH}
          opacity={0.42}
          samples={sourceSamples}
          y={REPATHING_SOURCE_Y}
        />
      ) : null}
      {curveSamples.length > 1 ? (
        <RepathingPathLine
          color={REPATHING_COLORS.curve}
          lineWidth={REPATHING_CURVE_LINE_WIDTH}
          opacity={0.92}
          samples={curveSamples}
          y={REPATHING_CURVE_Y}
        />
      ) : null}
      {tool === 'curve' ? <RepathingCurveControls /> : <RepathingBrushPlane />}
    </group>
  );
}

function RepathingCurveControls() {
  const store = useStore();
  const curve = store.state.curve;
  if (!curve) return null;

  return (
    <>
      {curve.points.map((point, index) => (
        <RepathingControlPointGizmo
          key={point.id}
          endpoint={index === 0 || index === curve.points.length - 1}
          point={point}
        />
      ))}
    </>
  );
}

function RepathingControlPointGizmo({
  endpoint,
  point,
}: {
  endpoint: boolean;
  point: RepathingControlPoint;
}) {
  const pointPosition = { x: point.x, z: point.z };
  return (
    <>
      {point.inHandle ? (
        <>
          <RepathingPathLine
            color={REPATHING_COLORS.handleLine}
            lineWidth={REPATHING_HANDLE_LINE_WIDTH}
            opacity={0.48}
            points={[
              [point.inHandle.x, REPATHING_HANDLE_Y, point.inHandle.z],
              [pointPosition.x, REPATHING_CONTROL_Y, pointPosition.z],
            ]}
            worldUnits={false}
          />
          <RepathingTransformHandle
            color={REPATHING_COLORS.handle}
            handle="in"
            pointId={point.id}
            position={point.inHandle}
            radius={REPATHING_HANDLE_RADIUS}
            y={REPATHING_HANDLE_Y}
          />
        </>
      ) : null}
      {point.outHandle ? (
        <>
          <RepathingPathLine
            color={REPATHING_COLORS.handleLine}
            lineWidth={REPATHING_HANDLE_LINE_WIDTH}
            opacity={0.48}
            points={[
              [pointPosition.x, REPATHING_CONTROL_Y, pointPosition.z],
              [point.outHandle.x, REPATHING_HANDLE_Y, point.outHandle.z],
            ]}
            worldUnits={false}
          />
          <RepathingTransformHandle
            color={REPATHING_COLORS.handle}
            handle="out"
            pointId={point.id}
            position={point.outHandle}
            radius={REPATHING_HANDLE_RADIUS}
            y={REPATHING_HANDLE_Y}
          />
        </>
      ) : null}
      <RepathingTransformHandle
        color={endpoint ? REPATHING_COLORS.endpoint : REPATHING_COLORS.control}
        handle="point"
        pointId={point.id}
        position={pointPosition}
        radius={REPATHING_POINT_RADIUS}
        y={REPATHING_CONTROL_Y}
      />
    </>
  );
}

interface RepathingTransformHandleProps {
  color: string;
  handle: RepathingControlHandle;
  pointId: string;
  position: RepathingPoint2d;
  radius: number;
  y: number;
}

function RepathingTransformHandle({
  color,
  handle,
  pointId,
  position,
  radius,
  y,
}: RepathingTransformHandleProps) {
  const store = useStore();
  const camera = useThree((state) => state.camera);
  const gl = useThree((state) => state.gl);
  const playerStore = usePlayerStoreApi();
  const { cameraControls } = usePlayerCameraControls();
  const objectRef = useRef<Object3D | null>(null);
  const markerObjectRef = useRef<Object3D | null>(null);
  const dragRef = useRef<{
    before: RepathingActionSnapshot;
    pointerId: number;
    offset: RepathingPoint2d;
  } | null>(null);
  const previousCameraEnabledRef = useRef<boolean | null>(null);
  const worldPositionRef = useRef(new Vector3());
  const cameraPositionRef = useRef(new Vector3());
  const pointerRef = useRef(new Vector2());
  const raycasterRef = useRef(new Raycaster());
  const dragPlaneRef = useRef(new Plane(REPATHING_DRAG_PLANE_NORMAL, -y));
  const dragPlaneHitRef = useRef(new Vector3());
  const setObjectRef = useCallback((object: Object3D | null) => {
    objectRef.current = object;
  }, []);
  const selection = useMemo(
    () => ({ pointId, handle }) satisfies RepathingControlSelection,
    [handle, pointId],
  );
  const editActionLabel =
    handle === 'point' ? 'Move path point' : 'Move tangent handle';
  const opaqueBorder = color === REPATHING_COLORS.control;
  const hovered = isSameSelection(store.state.hoveredControl, selection);
  const selected = isSameSelection(store.state.selectedControl, selection);
  const generationPending = store.state.generation.status === 'pending';

  useEffect(() => {
    dragPlaneRef.current.constant = -y;
  }, [y]);

  useFrame(() => {
    const object = objectRef.current;
    const markerObject = markerObjectRef.current;
    if (!object || !markerObject) return;

    markerObject.scale.setScalar(
      getTransformControlsWorldScale({
        camera,
        object,
        cameraPosition: cameraPositionRef.current,
        worldPosition: worldPositionRef.current,
      }),
    );
  });

  const releaseCamera = useCallback(() => {
    const controls = cameraControls as CameraControlsWithEnabled | null;
    if (controls && previousCameraEnabledRef.current !== null) {
      controls.stop?.();
      controls.enabled = previousCameraEnabledRef.current;
    }
    previousCameraEnabledRef.current = null;
  }, [cameraControls]);

  const finishDrag = useCallback(() => {
    if (!dragRef.current) return;

    const drag = dragRef.current;
    dragRef.current = null;
    store.recordCurveEditAction({
      label: editActionLabel,
      before: drag.before,
    });
    try {
      gl.domElement.releasePointerCapture(drag.pointerId);
    } catch {
      // Pointer capture may already be released by the browser.
    }
    releaseCamera();
    store.setDragging(false);
    playerStore.setGizmoDragging(false);
  }, [editActionLabel, gl.domElement, playerStore, releaseCamera, store]);

  useEffect(() => {
    return () => {
      finishDrag();
    };
  }, [finishDrag]);

  useEffect(() => {
    if (generationPending) {
      finishDrag();
    }
  }, [finishDrag, generationPending]);

  const pointFromPointerEvent = useCallback(
    (event: PointerEvent) =>
      pointFromPointerEventOnHorizontalPlane({
        camera,
        domElement: gl.domElement,
        event,
        intersection: dragPlaneHitRef.current,
        plane: dragPlaneRef.current,
        pointer: pointerRef.current,
        raycaster: raycasterRef.current,
      }),
    [camera, gl.domElement],
  );

  const disableCamera = useCallback(() => {
    const controls = cameraControls as CameraControlsWithEnabled | null;
    if (!controls) return;

    if ('enabled' in controls) {
      previousCameraEnabledRef.current = controls.enabled ?? true;
      controls.enabled = false;
    }
    controls.cancel?.();
    controls.stop?.();
  }, [cameraControls]);

  const moveDrag = useCallback(
    (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;

      const point = pointFromPointerEvent(event);
      if (!point) return;

      event.preventDefault();
      const nextPosition = {
        x: point.x + drag.offset.x,
        z: point.z + drag.offset.z,
      };
      objectRef.current?.position.set(nextPosition.x, y, nextPosition.z);
      store.moveControl(selection, nextPosition);
    },
    [pointFromPointerEvent, selection, store, y],
  );

  useEffect(() => {
    const canvas = gl.domElement;
    canvas.addEventListener('pointermove', moveDrag);
    canvas.addEventListener('pointerup', finishDrag);
    canvas.addEventListener('pointercancel', finishDrag);
    canvas.addEventListener('pointerleave', finishDrag);

    return () => {
      canvas.removeEventListener('pointermove', moveDrag);
      canvas.removeEventListener('pointerup', finishDrag);
      canvas.removeEventListener('pointercancel', finishDrag);
      canvas.removeEventListener('pointerleave', finishDrag);
    };
  }, [finishDrag, gl.domElement, moveDrag]);

  const startDrag = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      if (generationPending || event.button !== 0) return;

      const point = pointFromPointerEvent(event.nativeEvent);
      if (!point) return;

      event.stopPropagation();
      event.nativeEvent.preventDefault();
      try {
        gl.domElement.setPointerCapture(event.nativeEvent.pointerId);
      } catch {
        // Pointer capture is best-effort; canvas-level listeners still handle drag.
      }
      store.setSelectedControl(selection);
      dragRef.current = {
        before: store.captureActionSnapshot(),
        pointerId: event.nativeEvent.pointerId,
        offset: {
          x: position.x - point.x,
          z: position.z - point.z,
        },
      };
      store.setDragging(true);
      playerStore.setGizmoDragging(true);
      disableCamera();
    },
    [
      disableCamera,
      generationPending,
      gl.domElement,
      playerStore,
      pointFromPointerEvent,
      position.x,
      position.z,
      selection,
      store,
    ],
  );

  return (
    <>
      <group
        ref={setObjectRef}
        position={[position.x, y, position.z]}
        renderOrder={REPATHING_RENDER_ORDER}
        onPointerDown={startDrag}
        onPointerOver={(event) => {
          if (generationPending) return;
          event.stopPropagation();
          store.setHoveredControl(selection);
        }}
        onPointerOut={() => {
          if (store.state.hoveredControl?.pointId === pointId) {
            store.setHoveredControl(null);
          }
        }}
      >
        <group ref={markerObjectRef}>
          <mesh renderOrder={REPATHING_RENDER_ORDER}>
            <sphereGeometry
              args={[getMarkerHitRadius(handle, radius), 12, 8]}
            />
            <meshBasicMaterial transparent opacity={0} depthWrite={false} />
          </mesh>
          <mesh renderOrder={REPATHING_RENDER_ORDER + 0.01}>
            <sphereGeometry
              args={[radius * REPATHING_MARKER_BORDER_SCALE, 18, 14]}
            />
            <meshBasicMaterial
              color="#ffffff"
              transparent
              opacity={opaqueBorder ? 1 : 0.52}
              depthTest
              depthWrite={false}
            />
          </mesh>
          <mesh renderOrder={REPATHING_RENDER_ORDER + 0.02}>
            <sphereGeometry args={[radius, 18, 14]} />
            <meshBasicMaterial
              color={hovered || selected ? getActiveMarkerColor(color) : color}
              transparent
              opacity={1}
              depthTest
              depthWrite={false}
            />
          </mesh>
        </group>
      </group>
    </>
  );
}

function getMarkerHitRadius(handle: RepathingControlHandle, radius: number) {
  return handle === 'point' ? radius : REPATHING_HANDLE_HIT_RADIUS;
}

function getActiveMarkerColor(color: string) {
  if (color === REPATHING_COLORS.control) {
    return REPATHING_COLORS.controlActive;
  }
  if (color === REPATHING_COLORS.endpoint) {
    return REPATHING_COLORS.endpointActive;
  }
  if (color === REPATHING_COLORS.handle) {
    return REPATHING_COLORS.handleActive;
  }

  return color;
}

function getTransformControlsWorldScale({
  camera,
  object,
  cameraPosition,
  worldPosition,
}: {
  camera: Camera;
  object: Object3D;
  cameraPosition: Vector3;
  worldPosition: Vector3;
}) {
  object.getWorldPosition(worldPosition);

  if (isOrthographicCamera(camera)) {
    return dampenMarkerWorldScale(
      ((camera.top - camera.bottom) / Math.max(camera.zoom, Number.EPSILON)) *
        (KEY_POSE_TRANSFORM_CONTROLS_SIZE / 6),
    );
  }

  camera.getWorldPosition(cameraPosition);
  return dampenMarkerWorldScale(
    worldPosition.distanceTo(cameraPosition) *
      (KEY_POSE_TRANSFORM_CONTROLS_SIZE / 7),
  );
}

function dampenMarkerWorldScale(scale: number) {
  if (scale <= 1) {
    return scale;
  }

  return Math.min(
    1 + (scale - 1) * REPATHING_MARKER_FAR_SCALE_DAMPING,
    REPATHING_MARKER_MAX_WORLD_SCALE,
  );
}

function isOrthographicCamera(camera: Camera): camera is OrthographicCamera {
  return (
    (camera as OrthographicCamera & { isOrthographicCamera?: boolean })
      .isOrthographicCamera === true
  );
}

function RepathingBrushPlane() {
  const gl = useThree((state) => state.gl);
  const store = useStore();
  const playerStore = usePlayerStoreApi();
  const { cameraControls } = usePlayerCameraControls();
  const [brushPosition, setBrushPosition] = useState<RepathingPoint2d | null>(
    null,
  );
  const dragRef = useRef<{
    before: RepathingActionSnapshot;
    pointerId: number;
    point: RepathingPoint2d;
  } | null>(null);
  const previousCameraEnabledRef = useRef<boolean | null>(null);
  const disabled = store.state.generation.status === 'pending';

  const releaseCamera = useCallback(() => {
    const controls = cameraControls as CameraControlsWithEnabled | null;
    if (controls && previousCameraEnabledRef.current !== null) {
      controls.stop?.();
      controls.enabled = previousCameraEnabledRef.current;
    }
    previousCameraEnabledRef.current = null;
  }, [cameraControls]);

  const finishBrush = useCallback(() => {
    if (!dragRef.current) return;

    const drag = dragRef.current;
    dragRef.current = null;
    store.recordCurveEditAction({
      label: 'Nudge path',
      before: drag.before,
    });
    store.setDragging(false);
    playerStore.setGizmoDragging(false);
    releaseCamera();
  }, [playerStore, releaseCamera, store]);

  useEffect(() => {
    const canvas = gl.domElement;
    canvas.addEventListener('pointerup', finishBrush);
    canvas.addEventListener('pointercancel', finishBrush);
    canvas.addEventListener('pointerleave', finishBrush);

    return () => {
      canvas.removeEventListener('pointerup', finishBrush);
      canvas.removeEventListener('pointercancel', finishBrush);
      canvas.removeEventListener('pointerleave', finishBrush);
      finishBrush();
    };
  }, [finishBrush, gl.domElement]);

  const startBrush = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      if (disabled || event.button !== 0) return;

      const point = pointFromThreeEvent(event);
      event.stopPropagation();
      setBrushPosition(point);
      store.setSelectedControl(null);
      dragRef.current = {
        before: store.captureActionSnapshot(),
        pointerId: event.nativeEvent.pointerId,
        point,
      };
      store.setDragging(true);
      playerStore.setGizmoDragging(true);

      const controls = cameraControls as CameraControlsWithEnabled | null;
      if (!controls) return;
      if ('enabled' in controls) {
        previousCameraEnabledRef.current = controls.enabled ?? true;
        controls.enabled = false;
      }
      controls.cancel?.();
      controls.stop?.();
    },
    [cameraControls, disabled, playerStore, store],
  );

  const moveBrush = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      if (disabled) return;

      const point = pointFromThreeEvent(event);
      setBrushPosition(point);
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.nativeEvent.pointerId) {
        return;
      }

      event.stopPropagation();
      store.applyBrush(point, {
        x: point.x - drag.point.x,
        z: point.z - drag.point.z,
      });
      drag.point = point;
    },
    [disabled, store],
  );

  return (
    <>
      <mesh
        position={[0, 0, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        renderOrder={REPATHING_RENDER_ORDER}
        onPointerDown={startBrush}
        onPointerMove={moveBrush}
        onPointerUp={finishBrush}
      >
        <planeGeometry
          args={[REPATHING_POINTER_PLANE_SIZE, REPATHING_POINTER_PLANE_SIZE]}
        />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
      {brushPosition ? (
        <mesh
          position={[brushPosition.x, REPATHING_CONTROL_Y, brushPosition.z]}
          rotation={[-Math.PI / 2, 0, 0]}
          raycast={ignoreRaycast}
          renderOrder={REPATHING_RENDER_ORDER}
        >
          <ringGeometry
            args={[
              store.state.brushRadius,
              store.state.brushRadius + 0.018,
              72,
            ]}
          />
          <meshBasicMaterial
            color={REPATHING_COLORS.selected}
            transparent
            opacity={0.56}
            depthTest
            depthWrite={false}
          />
        </mesh>
      ) : null}
    </>
  );
}

interface RepathingPathLineProps {
  color: string;
  lineWidth: number;
  opacity: number;
  points?: readonly (readonly [number, number, number])[];
  samples?: readonly RepathingPoint2d[];
  worldUnits?: boolean;
  y?: number;
}

function RepathingPathLine({
  color,
  lineWidth,
  opacity,
  points: explicitPoints,
  samples,
  worldUnits = true,
  y,
}: RepathingPathLineProps) {
  const points = useMemo(
    () =>
      explicitPoints ??
      samples?.map((sample) => [sample.x, y ?? 0, sample.z] as const) ??
      [],
    [explicitPoints, samples, y],
  );

  return (
    <Line
      color={color}
      depthTest
      depthWrite={false}
      lineWidth={lineWidth}
      opacity={opacity}
      points={points}
      raycast={ignoreRaycast}
      renderOrder={REPATHING_RENDER_ORDER}
      transparent
      worldUnits={worldUnits}
    />
  );
}

function pointFromPointerEventOnHorizontalPlane({
  camera,
  domElement,
  event,
  intersection,
  plane,
  pointer,
  raycaster,
}: {
  camera: Camera;
  domElement: HTMLCanvasElement;
  event: PointerEvent;
  intersection: Vector3;
  plane: Plane;
  pointer: Vector2;
  raycaster: Raycaster;
}) {
  const rect = domElement.getBoundingClientRect();
  pointer.set(
    ((event.clientX - rect.left) / rect.width) * 2 - 1,
    -(((event.clientY - rect.top) / rect.height) * 2 - 1),
  );
  raycaster.setFromCamera(pointer, camera);

  if (!raycaster.ray.intersectPlane(plane, intersection)) {
    return null;
  }

  return {
    x: intersection.x,
    z: intersection.z,
  };
}

function pointFromThreeEvent(event: ThreeEvent<PointerEvent>) {
  return {
    x: event.point.x,
    z: event.point.z,
  };
}

function isSameSelection(
  left: RepathingControlSelection | null,
  right: RepathingControlSelection,
) {
  return left?.pointId === right.pointId && left.handle === right.handle;
}
